import React, { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { openDillToast } from '../../../../utils/helpers';
import {
  getCreditApplications,
  updateApplicationUpdateHistory,
  updateSupplierCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillButton } from '../../../general/Dill/DillButton';
import { DillDropdown } from '../../../general/Dill/DillDropdown';
import { getBranchSalesRepKey } from '@dill/dill-shared';

const AssignBranchModal = ({
  open,
  handleClose,
  creditApplicationId,
  reload = false,
  allowedBranchIds,
  lockBranch = false,
  onSuccess,
  applicationHistoryId,
  currentApplication,
}: {
  open: boolean;
  handleClose: () => void;
  creditApplicationId: string;
  reload?: boolean;
  allowedBranchIds?: string[];
  lockBranch?: boolean;
  onSuccess?: () => void;
  // Set by a page showing one specific record (the details page): null targets the credit
  // application itself, an id targets that history version. Left undefined, the latest history
  // version from the list is used.
  applicationHistoryId?: string | null;
  currentApplication?: { branchId?: string | null; assignedSalesRep?: string[] } | null;
}) => {
  const dispatch = useAppDispatch();
  const [inputFields, setInputFields] = useState<{
    selectedbranchId: string;
    selectedRep: string;
  }>({ selectedbranchId: '', selectedRep: '' });
  const [errorFields, setErrorFields] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { creditApplications } = useAppSelector((state) => state.creditApplications);
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);

  const handleCloseModal = () => {
    setInputFields({ selectedbranchId: '', selectedRep: '' });
    setErrorFields({});
    setIsLoading(false);
    handleClose();
  };

  const handleFieldChange = (field: keyof typeof inputFields, value: string) => {
    setInputFields((prev) => {
      const update = { [field]: value };
      if (field === 'selectedbranchId' && prev?.[field] !== value) {
        return { ...prev, ...update, selectedRep: '' };
      }
      return { ...prev, ...update };
    });
    setErrorFields((prev) => ({ ...prev, [field]: '' }));
  };

  const resolveApplication = () => {
    if (applicationHistoryId !== undefined) {
      return {
        branchId: currentApplication?.branchId,
        assignedSalesRep: currentApplication?.assignedSalesRep,
        applicationUpdateHistoryId: applicationHistoryId,
      };
    }
    const creditApplication = creditApplications.find((app) => app.id === creditApplicationId);
    if (!creditApplication) return null;
    const latestHistory = creditApplication.applicationUpdateHistory?.[0];
    if (latestHistory) {
      return { ...latestHistory, applicationUpdateHistoryId: latestHistory.id };
    }
    return { ...creditApplication, applicationUpdateHistoryId: null };
  };

  useEffect(() => {
    if (creditApplicationId && open) {
      const application = resolveApplication();
      if (application?.branchId) {
        setInputFields((prev) => ({ ...prev, selectedbranchId: application.branchId ?? '' }));
      }
      if (application?.assignedSalesRep) {
        setInputFields((prev) => ({
          ...prev,
          selectedRep: application.assignedSalesRep?.[0] ?? '',
        }));
      }
    }
  }, [creditApplicationId, creditApplications, open, applicationHistoryId, currentApplication]);

  const validateFields = () => {
    const errors: { [key: string]: string } = {};
    if (!lockBranch && !inputFields.selectedbranchId.trim()) {
      errors.selectedbranchId = 'Select a branch';
    }

    setErrorFields(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;
    setIsLoading(true);

    let data = {};
    if (!lockBranch && inputFields.selectedbranchId) {
      data = { ...data, branchId: inputFields.selectedbranchId };
    }
    if (inputFields.selectedRep) {
      data = { ...data, assignedSalesRep: [inputFields.selectedRep] };
    }
    let results = null;
    const application = resolveApplication();
    if (application?.applicationUpdateHistoryId) {
      results = await dispatch(
        updateApplicationUpdateHistory({
          creditApplicationId: creditApplicationId,
          applicationHistoryId: application?.applicationUpdateHistoryId,
          supplierId: currentSupplier?.id || '',
          ...data,
        })
      );
    } else {
      results = await dispatch(
        updateSupplierCreditApplication({
          creditApplicationId: creditApplicationId,
          supplierId: currentSupplier?.id || '',
          ...data,
        })
      );
    }
    if (results && results.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `Successfully added branch to the application`,
      });
      if (reload) {
        await dispatch(getCreditApplications({ supplierId: currentSupplier?.id ?? '' }));
      }
      onSuccess?.();
    } else {
      openDillToast({
        type: 'ERROR',
        message: `Failed to add branch to the application`,
      });
    }
    handleCloseModal();
    setIsLoading(false);
  };

  const branches = useMemo(() => {
    const all = supplierBranches;
    if (allowedBranchIds) {
      return all.filter((b) => allowedBranchIds.includes(b.branchId));
    }
    return all;
  }, [user, allowedBranchIds, supplierBranches]);

  const salesRep = useMemo(() => {
    if (supplierBranches.length > 0 && inputFields.selectedbranchId) {
      const selectedBranch = supplierBranches.find(
        (branch) => branch.branchId === inputFields.selectedbranchId
      );
      // Includes reps with no Dill account (keyed by ERP id — see getBranchSalesRepKey).
      return (selectedBranch?.salesRep ?? []).filter((rep) => !!getBranchSalesRepKey(rep));
    }
    return [];
  }, [user, inputFields.selectedbranchId, supplierBranches]);

  const currentBranchName = useMemo(() => {
    if (!lockBranch) return null;
    const branch = supplierBranches.find((b) => b.branchId === inputFields.selectedbranchId);
    return branch?.branchName ?? null;
  }, [lockBranch, inputFields.selectedbranchId, user, supplierBranches]);

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title={lockBranch ? 'Assign Sales Rep' : 'Assign Branch & Rep'}
      footer={
        <div className="flex justify-end gap-4">
          <DillButton
            label="Cancel"
            variant="secondary"
            onClick={handleCloseModal}
            isDisabled={isLoading}
          />
          <DillButton
            label={lockBranch ? 'Assign Sales Rep' : 'Assign Branch & Rep'}
            onClick={handleSubmit}
            isDisabled={isLoading}
            isLoading={isLoading}
          />
        </div>
      }>
      <div className="flex flex-col gap-4">
        {lockBranch ? (
          currentBranchName && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-600">Branch</span>
              <div className="h-[36px] flex items-center px-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
                {currentBranchName}
              </div>
            </div>
          )
        ) : (
          <DillDropdown
            isRequired
            containerClassName="h-[36px]"
            value={inputFields.selectedbranchId}
            errorText={errorFields.selectedbranchId ?? ''}
            items={branches.map((item) => ({
              label: item?.branchName ?? '',
              value: item.branchId,
              labelInElement: (
                <div className="flex m-2 items-center">
                  <div className="text-black text-sm">{item.branchName}</div>
                </div>
              ),
            }))}
            label="Select Branch"
            onSelectChange={(item) => {
              if (item?.value) {
                handleFieldChange('selectedbranchId', item.value);
              }
            }}
          />
        )}
        <DillDropdown
          isSearchable={true}
          isCreatable={false}
          label="Select Sales Rep"
          value={inputFields.selectedRep}
          containerClassName="h-[36px]"
          items={salesRep.map((item) => ({
            label: item?.name ?? '',
            value: getBranchSalesRepKey(item),
            labelInElement: (
              <div className="flex m-1 items-center">
                <div>
                  <div className="text-black text-sm">{item?.name ?? ''}</div>
                  <div className="TEXT_SECONDARY-CLR text-xs">{item?.email ?? ''}</div>
                </div>
              </div>
            ),
          }))}
          showMinimalDesign
          onSelectChange={(item) => {
            if (item?.value) {
              handleFieldChange('selectedRep', item.value);
            }
          }}
        />
      </div>
    </DillModal>
  );
};

export default AssignBranchModal;
